import {startTransition, useCallback, useEffect, useMemo, useState} from 'react';
import {Box, Button, Skeleton, Stack, Typography} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import {SplitTabsPanel} from '@/components/common';
import {
    AuditProgramSectionVO,
    getCSectionVO,
    saveCSectionPage,
} from '@/services/auditService/auditProgramService';
import C7MaterialitySummary from '@/pages/components/audit-program/C7-MaterialitySummary';
import C71DeterminingMateriality from '@/pages/components/audit-program/C71-DeterminingMateriality';
import {
    C71VO,
    formatAmount,
    getC71VO,
    performanceMateriality,
} from '@/services/auditService/materialityService';
import {
    C1_CONTENT,
    C1A_CONTENT,
    withC1Defaults,
    C21_PAGE,
    C_CHECKLIST_PAGES,
    C_PAGE_TITLES,
    C_SECTION_INDEX,
    C_SECTION_INDEX_LETTER,
    C_SECTION_INDEX_TITLE,
    C_SECTION_PAGES,
    CChecklistPage,
    CChecklistPageCode,
    CSectionPageCode,
    withCodePropKinds,
} from '@/pages/components/audit-program/c-section/constants';
import {withCPageDefaults} from '@/pages/components/audit-program/c-section/defaults';
import CChecklistView from '@/pages/components/audit-program/c-section/CChecklistView';
import C1MemorandumView from '@/pages/components/audit-program/c-section/C1MemorandumView';
import C1aAcceptanceView from '@/pages/components/audit-program/c-section/C1aAcceptanceView';
import C21IndependenceView from '@/pages/components/audit-program/c-section/C21IndependenceView';
import C22SafeguardsView from '@/pages/components/audit-program/c-section/C22SafeguardsView';
import C41AccountingEstimatesView from '@/pages/components/audit-program/c-section/C41AccountingEstimatesView';
import C51ControlsView from '@/pages/components/audit-program/c-section/C51ControlsView';
import C52QuestionnaireView from '@/pages/components/audit-program/c-section/C52QuestionnaireView';
import C81RiskAssessmentView from '@/pages/components/audit-program/c-section/C81RiskAssessmentView';
import C82RiskActionPlanView from '@/pages/components/audit-program/c-section/C82RiskActionPlanView';
import C83AssertionRiskView from '@/pages/components/audit-program/c-section/C83AssertionRiskView';
import C84RiskResponseSummaryView from '@/pages/components/audit-program/c-section/C84RiskResponseSummaryView';
import C91FraudRiskView from '@/pages/components/audit-program/c-section/C91FraudRiskView';
import C133TimetableView from '@/pages/components/audit-program/c-section/C133TimetableView';
import C134BudgetView from '@/pages/components/audit-program/c-section/C134BudgetView';
import C135ProgressView from '@/pages/components/audit-program/c-section/C135ProgressView';
import PageTabLabel from '@/pages/components/audit-program/PageTabLabel';
import SectionIndexView from '@/pages/components/audit-program/SectionIndexView';
import {useProgramSectionTemplates} from '@/pages/components/audit-program/templates/useProgramSectionTemplates';

export type CSectionViewProps = {
    companyId: number;
    annualId: number;
    companyName?: string | null;
    initialPage?: CSectionPageCode;
};

function toStringRecord(data?: Record<string, unknown>): Record<string, string> {
    if (!data) return {};
    return Object.fromEntries(
        Object.entries(data).map(([k, v]) => [k, v == null ? '' : String(v)]),
    );
}

const MATERIALITY_PAGES = new Set<CSectionPageCode>(['C7', 'C7.1']);
const DISPLAY_ONLY_PAGES = new Set<CSectionPageCode>(['Ci', 'C7', 'C7.1']);

/**
 * Audit Program — C section (Audit Planning).
 * Left page tabs like A/B; C7/C7.1 keep computed materiality (display-only).
 */
export default function CSectionView({
    companyId,
    annualId,
    companyName,
    initialPage = 'Ci',
}: CSectionViewProps) {
    const [pageKey, setPageKey] = useState<CSectionPageCode>(initialPage);
    const [header, setHeader] = useState<AuditProgramSectionVO | null>(null);
    const [pageData, setPageData] = useState<Record<string, Record<string, string>>>({});
    const [c71, setC71] = useState<C71VO | null>(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [dirty, setDirty] = useState<Record<string, boolean>>({});
    const {getContent} = useProgramSectionTemplates('C');

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [sectionData, materiality] = await Promise.all([
                getCSectionVO(companyId, annualId),
                getC71VO(companyId, annualId).catch(() => null),
            ]);
            if (companyName && !sectionData.client) {
                sectionData.client = companyName;
            }
            setHeader(sectionData);
            setC71(materiality);
            const pages: Record<string, Record<string, string>> = {};
            for (const code of C_SECTION_PAGES) {
                if (MATERIALITY_PAGES.has(code) || code === 'Ci') {
                    if (code === 'Ci') pages[code] = toStringRecord(sectionData.pages?.[code]);
                    continue;
                }
                const raw = withCPageDefaults(
                    code,
                    toStringRecord(sectionData.pages?.[code]),
                    sectionData,
                );
                if (code === 'C1') {
                    const c1Template = getContent('C1') as {
                        followUpSections?: Array<{key?: string; defaultText?: string}>;
                    } | undefined;
                    pages[code] = withC1Defaults(raw, c1Template);
                } else {
                    pages[code] = raw;
                }
            }
            setPageData(pages);
            setDirty({});
        } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : 'Failed to load C Section';
            setError(msg);
            setHeader(null);
        } finally {
            setLoading(false);
        }
    }, [companyId, annualId, companyName, getContent]);

    useEffect(() => {
        void load();
    }, [load]);

    useEffect(() => {
        setPageKey(initialPage);
    }, [initialPage]);

    const handleFieldChange = useCallback((pageCode: CSectionPageCode, key: string, value: string) => {
        setPageData((prev) => ({
            ...prev,
            [pageCode]: {...(prev[pageCode] ?? {}), [key]: value},
        }));
        setDirty((prev) => ({...prev, [pageCode]: true}));
    }, []);

    const handleSave = useCallback(async () => {
        if (DISPLAY_ONLY_PAGES.has(pageKey) || !dirty[pageKey]) return;
        setSaving(true);
        setError(null);
        try {
            await saveCSectionPage(companyId, annualId, pageKey, pageData[pageKey] ?? {});
            setDirty((prev) => ({...prev, [pageKey]: false}));
        } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : 'Failed to save';
            setError(msg);
        } finally {
            setSaving(false);
        }
    }, [companyId, annualId, dirty, pageKey, pageData]);

    const overallInitial = Number(c71?.turnMatLevel ?? 0);
    const overallFinal = Number(c71?.turnFinMatLevel ?? 0);
    const performanceBySection = useMemo(() => ({
        ...Object.fromEntries(
            ['E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M'].map((code) => [
                code,
                {
                    initial: formatAmount(performanceMateriality(overallInitial, 1.2)),
                    final: formatAmount(performanceMateriality(overallFinal, 1.2)),
                },
            ]),
        ),
        'R-income': {
            initial: formatAmount(performanceMateriality(overallInitial, 1.6)),
            final: formatAmount(performanceMateriality(overallFinal, 1.6)),
        },
        'R-expenses': {
            initial: formatAmount(performanceMateriality(overallInitial, 1.2)),
            final: formatAmount(performanceMateriality(overallFinal, 1.2)),
        },
        'R-directors': {
            initial: formatAmount(performanceMateriality(overallInitial, 1.2)),
            final: formatAmount(performanceMateriality(overallFinal, 1.2)),
        },
        'R-related': {
            initial: formatAmount(performanceMateriality(overallInitial, 1.2)),
            final: formatAmount(performanceMateriality(overallFinal, 1.2)),
        },
    }), [overallInitial, overallFinal]);

    const renderPage = useCallback((code: CSectionPageCode) => {
        if (!header) return null;
        const templatePage = getContent(code);
        if (code === 'Ci') {
            return (
                <SectionIndexView
                    header={header}
                    refCode="Ci"
                    sectionLetter={C_SECTION_INDEX_LETTER}
                    sectionTitle={C_SECTION_INDEX_TITLE}
                    rows={C_SECTION_INDEX}
                    onNavigate={(pageCode) => startTransition(() => setPageKey(pageCode as CSectionPageCode))}
                />
            );
        }
        if (code === 'C1') {
            return (
                <C1MemorandumView
                    header={header}
                    values={pageData.C1 ?? {}}
                    onChange={(k, v) => handleFieldChange('C1', k, v)}
                    content={(templatePage ?? C1_CONTENT) as typeof C1_CONTENT}
                />
            );
        }
        if (code === 'C1a') {
            return (
                <C1aAcceptanceView
                    header={header}
                    values={pageData.C1a ?? {}}
                    onChange={(k, v) => handleFieldChange('C1a', k, v)}
                    content={(templatePage ?? C1A_CONTENT) as typeof C1A_CONTENT}
                />
            );
        }
        if (code === 'C2.1') {
            return (
                <C21IndependenceView
                    header={header}
                    values={pageData['C2.1'] ?? {}}
                    onChange={(k, v) => handleFieldChange('C2.1', k, v)}
                    content={(templatePage ?? C21_PAGE) as typeof C21_PAGE}
                />
            );
        }
        if (code === 'C2.2') {
            return (
                <C22SafeguardsView
                    header={header}
                    values={pageData['C2.2'] ?? {}}
                    onChange={(k, v) => handleFieldChange('C2.2', k, v)}
                />
            );
        }
        if (code === 'C4.1') {
            return (
                <C41AccountingEstimatesView
                    header={header}
                    values={pageData['C4.1'] ?? {}}
                    onChange={(k, v) => handleFieldChange('C4.1', k, v)}
                />
            );
        }
        if (code === 'C5.1') {
            return (
                <C51ControlsView
                    header={header}
                    values={pageData['C5.1'] ?? {}}
                    onChange={(k, v) => handleFieldChange('C5.1', k, v)}
                />
            );
        }
        if (code === 'C5.2') {
            return (
                <C52QuestionnaireView
                    header={header}
                    values={pageData['C5.2'] ?? {}}
                    onChange={(k, v) => handleFieldChange('C5.2', k, v)}
                />
            );
        }
        if (code === 'C8.1') {
            return (
                <C81RiskAssessmentView
                    header={header}
                    values={pageData['C8.1'] ?? {}}
                    onChange={(k, v) => handleFieldChange('C8.1', k, v)}
                />
            );
        }
        if (code === 'C7') {
            return (
                <Box sx={{'& input, & textarea': {pointerEvents: 'none', cursor: 'default'}}}>
                    <C7MaterialitySummary
                        client={c71?.client || companyName || header.client || undefined}
                        yearEnd={c71?.yearEnd || header.yearEnd}
                        preparedBy={c71?.preparedBy || header.preparedBy}
                        reviewedBy={c71?.reviewedBy || header.reviewedBy}
                        overallInitial={formatAmount(overallInitial)}
                        overallFinal={formatAmount(overallFinal)}
                        performanceBySection={performanceBySection}
                    />
                </Box>
            );
        }
        if (code === 'C7.1') {
            if (!c71) {
                return (
                    <Box sx={{p: 2}}>
                        <Typography color="text.secondary">Materiality data unavailable.</Typography>
                    </Box>
                );
            }
            return (
                <Box sx={{p: 2, '& input, & textarea': {pointerEvents: 'none', cursor: 'default'}}}>
                    <C71DeterminingMateriality data={c71}/>
                </Box>
            );
        }
        if (code === 'C8.2') {
            return (
                <C82RiskActionPlanView
                    header={header}
                    values={pageData['C8.2'] ?? {}}
                    onChange={(k, v) => handleFieldChange('C8.2', k, v)}
                />
            );
        }
        if (code === 'C8.3') {
            return (
                <C83AssertionRiskView
                    header={header}
                    values={pageData['C8.3'] ?? {}}
                    onChange={(k, v) => handleFieldChange('C8.3', k, v)}
                />
            );
        }
        if (code === 'C8.4') {
            return (
                <C84RiskResponseSummaryView
                    header={header}
                    values={pageData['C8.4'] ?? {}}
                    onChange={(k, v) => handleFieldChange('C8.4', k, v)}
                />
            );
        }
        if (code === 'C9.1') {
            return (
                <C91FraudRiskView
                    header={header}
                    values={pageData['C9.1'] ?? {}}
                    onChange={(k, v) => handleFieldChange('C9.1', k, v)}
                />
            );
        }
        if (code === 'C13.3') {
            return (
                <C133TimetableView
                    header={header}
                    values={pageData['C13.3'] ?? {}}
                    onChange={(k, v) => handleFieldChange('C13.3', k, v)}
                />
            );
        }
        if (code === 'C13.4') {
            return (
                <C134BudgetView
                    header={header}
                    values={pageData['C13.4'] ?? {}}
                    onChange={(k, v) => handleFieldChange('C13.4', k, v)}
                />
            );
        }
        if (code === 'C13.5') {
            return (
                <C135ProgressView
                    header={header}
                    values={pageData['C13.5'] ?? {}}
                    onChange={(k, v) => handleFieldChange('C13.5', k, v)}
                />
            );
        }
        return (
            <CChecklistView
                header={header}
                page={withCodePropKinds(
                    (templatePage ?? C_CHECKLIST_PAGES[code as CChecklistPageCode]) as CChecklistPage,
                    C_CHECKLIST_PAGES[code as CChecklistPageCode] as CChecklistPage | undefined,
                )}
                refCode={code}
                values={pageData[code] ?? {}}
                onChange={(k, v) => handleFieldChange(code, k, v)}
            />
        );
    }, [
        header,
        c71,
        companyName,
        overallInitial,
        overallFinal,
        performanceBySection,
        pageData,
        handleFieldChange,
        getContent,
    ]);

    const tabs = useMemo(() => C_SECTION_PAGES.map((code) => ({
        key: code,
        label: <PageTabLabel code={code} title={C_PAGE_TITLES[code]}/>,
        icon: code,
        // Only mount the active page — C1a alone has ~350 fields; eager-building all pages stalls tab clicks.
        content: code === pageKey ? renderPage(code) : null,
    })), [renderPage, pageKey]);

    if (loading) {
        return <Skeleton variant="rectangular" height={480} sx={{m: 2}}/>;
    }

    if (error && !header) {
        return (
            <Box sx={{p: 2}}>
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }

    const canSave = !DISPLAY_ONLY_PAGES.has(pageKey) && Boolean(dirty[pageKey]);

    return (
        <Box sx={{height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column'}}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{px: 2, pt: 1, pb: 0.5}}>
                <Typography variant="subtitle1" fontWeight={600}>
                    C Section — Audit Planning
                </Typography>
                <Button
                    variant="contained"
                    size="small"
                    startIcon={<SaveIcon/>}
                    disabled={!canSave || saving}
                    onClick={() => void handleSave()}
                >
                    {DISPLAY_ONLY_PAGES.has(pageKey) ? 'Display only' : `Save ${pageKey}`}
                </Button>
            </Stack>
            {error ? (
                <Typography color="error" sx={{px: 2, pb: 1}} variant="body2">{error}</Typography>
            ) : null}
            <Box sx={{flex: 1, minHeight: 0}}>
                <SplitTabsPanel
                    tabs={tabs}
                    selectedKey={pageKey}
                    onChange={(k) => startTransition(() => setPageKey(k as CSectionPageCode))}
                    height="100%"
                    allowMaximize={false}
                    leftPaneSx={{minWidth: 180}}
                    contentWrapperSx={{height: '100%', overflow: 'auto', p: 0}}
                    rightPaneSx={{height: '100%', overflow: 'hidden'}}
                />
            </Box>
        </Box>
    );
}
